"use client";

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { CalendarDays, Car, ClipboardList, ReceiptText, UserRound } from "lucide-react";

import { AppDialog } from "@/components/ui/app-dialog";
import { formatVehiclePlate } from "@/lib/formatters";
import { apiClient } from "@/lib/api-client";
import { LineItemType } from "@/types/line-item-type";
import type { Quote } from "@/types/quote";
import { QuoteStatus } from "@/types/quote-status";

export type QuoteDetailsDialogProps = {
  quoteId: string | null;
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  backLabel?: string;
  onOpenClient?: (clientId: string) => void;
  onOpenVehicle?: (vehicleId: string) => void;
};

const statusLabel: Record<QuoteStatus, string> = {
  [QuoteStatus.PendingApproval]: "Aguardando aprovação",
  [QuoteStatus.Approved]: "Aprovado",
  [QuoteStatus.Rejected]: "Rejeitado",
  [QuoteStatus.AdjustmentRequested]: "Ajuste solicitado",
};

const statusClass: Record<QuoteStatus, string> = {
  [QuoteStatus.PendingApproval]: "bg-amber-50 text-amber-700",
  [QuoteStatus.Approved]: "bg-emerald-50 text-emerald-700",
  [QuoteStatus.Rejected]: "bg-red-50 text-red-700",
  [QuoteStatus.AdjustmentRequested]: "bg-blue-50 text-blue-700",
};

const itemTypeLabel: Record<LineItemType, string> = {
  [LineItemType.Part]: "Peça",
  [LineItemType.Service]: "Serviço",
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function DetailLabel({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-800/60">
      <span className="mt-0.5 text-slate-400">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <div className="mt-0.5 text-sm font-medium text-slate-800 dark:text-slate-100">{children}</div>
      </div>
    </div>
  );
}

export function QuoteDetailsDialog({
  quoteId,
  open,
  onClose,
  onBack,
  backLabel = "Voltar",
  onOpenClient,
  onOpenVehicle,
}: QuoteDetailsDialogProps) {
  const quoteQuery = useQuery({
    queryKey: ["quote", quoteId],
    enabled: open && Boolean(quoteId),
    queryFn: async () => {
      const response = await apiClient.get<Quote>(`/orcamentos/${quoteId}`);
      return response.data;
    },
  });

  const quote = quoteQuery.data;
  const laborCost = Number(quote?.laborCost ?? 0);
  const discount = Number(quote?.discount ?? 0);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={quote ? `Orçamento ${quote.number}` : "Visualizar orçamento"}
      description={
        quote?.createdAt
          ? `Criado em ${new Date(quote.createdAt).toLocaleString("pt-BR")}`
          : "Dados completos do orçamento selecionado"
      }
      size="xl"
      mobileFullscreen
      actions={
        <>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {backLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Fechar
          </button>
        </>
      }
    >
      {quoteQuery.isLoading ? (
        <div className="py-12 text-center text-sm text-slate-500">Carregando orçamento...</div>
      ) : quoteQuery.isError ? (
        <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>Não foi possível carregar os dados deste orçamento.</p>
          <button
            type="button"
            onClick={() => quoteQuery.refetch()}
            className="font-semibold underline underline-offset-2"
          >
            Tentar novamente
          </button>
        </div>
      ) : quote ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total do orçamento</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {currencyFormatter.format(Number(quote.total))}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass[quote.status]}`}>
              {statusLabel[quote.status]}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <DetailLabel icon={<UserRound className="h-4 w-4" />} label="Cliente">
              {onOpenClient ? (
                <button
                  type="button"
                  onClick={() => onOpenClient(quote.client.id)}
                  className="text-left font-semibold underline-offset-2 transition hover:text-[#E8441A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]"
                  aria-label={`Abrir cliente ${quote.client.name}`}
                >
                  {quote.client.name}
                </button>
              ) : quote.client.name}
            </DetailLabel>
            <DetailLabel icon={<Car className="h-4 w-4" />} label="Veículo">
              {onOpenVehicle ? (
                <button
                  type="button"
                  onClick={() => onOpenVehicle(quote.vehicle.id)}
                  className="block max-w-full truncate text-left font-semibold underline-offset-2 transition hover:text-[#E8441A] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]"
                  aria-label={`Abrir veículo ${quote.vehicle.plate}`}
                >
                  {formatVehiclePlate(quote.vehicle.plate)} · {quote.vehicle.brand} {quote.vehicle.model}
                </button>
              ) : (
                <span className="block truncate">
                  {formatVehiclePlate(quote.vehicle.plate)} · {quote.vehicle.brand} {quote.vehicle.model}
                </span>
              )}
            </DetailLabel>
            <DetailLabel icon={<CalendarDays className="h-4 w-4" />} label="Validade">
              {quote.validUntil
                ? new Date(quote.validUntil).toLocaleDateString("pt-BR", { timeZone: "UTC" })
                : "Não informada"}
            </DetailLabel>
            <DetailLabel icon={<ReceiptText className="h-4 w-4" />} label="Assinatura">
              {quote.assinaturaClienteUrl ? "Assinado digitalmente" : "Sem assinatura"}
            </DetailLabel>
          </div>

          {quote.notes ? (
            <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Observações</h3>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600 dark:text-slate-300">{quote.notes}</p>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Itens do orçamento</h3>
              <span className="text-xs text-slate-500">{quote.items.length} item(ns)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3 text-right">Qtd.</th>
                    <th className="px-4 py-3 text-right">Unitário</th>
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {quote.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">
                        {item.description ?? item.itemName ?? "Item sem descrição"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{itemTypeLabel[item.type]}</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">
                        {currencyFormatter.format(Number(item.unitPrice))}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800 dark:text-slate-100">
                        {currencyFormatter.format(Number(item.total))}
                      </td>
                    </tr>
                  ))}
                  {laborCost > 0 ? (
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">Mão de obra</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">Serviço</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">—</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">—</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800 dark:text-slate-100">
                        {currencyFormatter.format(laborCost)}
                      </td>
                    </tr>
                  ) : null}
                  {discount > 0 ? (
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">Desconto</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">—</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">—</td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">—</td>
                      <td className="px-4 py-3 text-right font-semibold text-red-600">
                        − {currencyFormatter.format(discount)}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {quote.items.length === 0 && laborCost <= 0 ? (
              <p className="px-4 py-5 text-sm text-slate-500">Nenhum item lançado neste orçamento.</p>
            ) : null}
          </section>
        </div>
      ) : null}
    </AppDialog>
  );
}
